import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import withWidgetConfig from '@sport1/sport1-components/build/widgets/config/component'
import widget from '@sport1/sport1-components/build/widgets/Universal/ConferenceTicker'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import { ConferenceTickerWidgetProps, LabelProps, TagProps } from '@sport1/types/web'
import { MatchClientModel, PlayerClientModel, SeasonClientModel } from '@sport1/types/sportData'
import { BettingActionProperties } from '@sport1/bets-service-client'
import {
    createPlayerInfoHref,
    createTeamHref,
    getMatchLivetickerHref,
} from '@/utils/navigation/Navigator'
import Liveticker from '@/components/Widget/Liveticker'
import getSeasonsBySport from '@/utils/sportsData/getSeasonsBySport'
import { log } from '@/utils/Logger'
import { handleBettingAction } from '@/helpers/bettingHelper'
import useBettingTracking from '@/hooks/useBettingTracking'

const ConferenceTickerWidget = withWidgetConfig(widget)

type ConferenceTickerProps = ConferenceTickerWidgetProps & {
    tag?: TagProps | LabelProps
}

const LIVETICKER_SPORTS = ['motorsport', 'cycling']

const ConferenceTicker: FC<ConferenceTickerProps> = ({
    tag,
    sportIdentifier,
    sportId,
    ...rest
}) => {
    const [seasons, setSeasons] = useState<SeasonClientModel[] | undefined>()
    // TODO: we can not update bet-service-client because of sport1-components, maybe using S1_WIDGETS like in the App?
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { trackBetImpression, trackBetClick } = useBettingTracking('quotenbanner_konferenzticker')

    const showLiveticker = useMemo(
        () => LIVETICKER_SPORTS.includes(sportIdentifier),
        [sportIdentifier]
    )

    useEffect(() => {
        if (showLiveticker) {
            getSeasonsBySport({ sportIdentifier, sportId })
                .then(setSeasons)
                .catch(error => log('Cannot get seasons', error, { sportIdentifier, sportId }))
        }
    }, [showLiveticker, sportIdentifier, sportId])

    const bettingActionHandler = useCallback(
        (bettingActionProperties: BettingActionProperties) => {
            handleBettingAction({
                bettingActionProperties,
                ignoreMatchStatus: true,
                trackBetImpression,
                trackBetClick,
            })
        },
        [trackBetClick, trackBetImpression]
    )

    if (showLiveticker) {
        return seasons && seasons[0] ? (
            <Liveticker
                tag={tag}
                seasonId={seasons[0].id}
                sportIdentifier={sportIdentifier}
                sportId={sportId}
                {...rest}
            />
        ) : null
    }

    return (
        <NonFlexingContainer paddingY={['spacing-6', 'spacing-6', 'spacing-9', 'spacing-none']}>
            <ConferenceTickerWidget
                createTeamHref={createTeamHref}
                createPlayerHref={(player: PlayerClientModel) => createPlayerInfoHref(player)}
                createMatchHref={(match: MatchClientModel) =>
                    getMatchLivetickerHref({
                        match,
                        tag,
                    })
                }
                bettingWidgetTrackingIdentifier="quotenbanner_konferenzticker"
                bettingActionHandler={bettingActionHandler}
                {...rest}
            />
        </NonFlexingContainer>
    )
}

export default ConferenceTicker
